import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import type { ReactNode } from 'react';
import { SwimlaneCustomerChip } from '@/components/common/custom-field-value/CustomerFieldDisplay';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import type { CustomerLookupEntry } from './types';

interface PublishedCustomFieldValueProps {
  field: CustomFieldDTO;
  value: unknown;
  customerLookup?: Record<string, CustomerLookupEntry>;
}

const formatValue = (field: CustomFieldDTO, value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  switch (field.type) {
    case 'FIELD_TYPE_TEXT':
    case 'FIELD_TYPE_MULTILINE_TEXT':
    case 'FIELD_TYPE_NOTE':
      return typeof value === 'string' ? value : String(value);

    case 'FIELD_TYPE_NUMERIC':
      return typeof value === 'number' ? value.toString() : String(value);

    case 'FIELD_TYPE_CURRENCY':
      if (typeof value === 'number') {
        const unit = field.currencyUnit || '$';
        return `${unit}${value.toLocaleString()}`;
      }
      return String(value);

    case 'FIELD_TYPE_DATE':
      if (typeof value === 'string') {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) {
          return String(value);
        }
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      }
      return String(value);

    case 'FIELD_TYPE_CHECKBOX':
      return value === true ? 'Yes' : value === false ? 'No' : '—';

    case 'FIELD_TYPE_PICKLIST':
      if (typeof value === 'number') {
        const option = field.picklistOptions.find((opt) => opt.id === value);
        return option?.name || String(value);
      }
      return String(value);

    case 'FIELD_TYPE_MULTI_PICKLIST':
      if (Array.isArray(value)) {
        const names = value
          .map((id) => {
            const option = field.picklistOptions.find((opt) => opt.id === id);
            return option?.name || String(id);
          })
          .filter(Boolean);
        return names.length > 0 ? names.join(', ') : '—';
      }
      return '—';

    case 'FIELD_TYPE_USER':
    case 'FIELD_TYPE_ITEM':
    case 'FIELD_TYPE_LINK':
      return typeof value === 'string' ? value : String(value);

    case 'FIELD_TYPE_MULTI_USER':
    case 'FIELD_TYPE_MULTI_ITEM':
    case 'FIELD_TYPE_MULTI_LINK':
    case 'FIELD_TYPE_MULTI_TEXT':
      if (Array.isArray(value)) {
        return value.length > 0 ? value.map(String).join(', ') : '—';
      }
      return '—';

    default:
      return String(value);
  }
};

/**
 * Extract customer ids from a published custom-field value, tolerating the
 * shapes the backend may emit:
 * - Bare numeric/string scalar or array: `5001` / `[5001, 5002]`
 * - `{ MULTI_PICKLIST: <id> }` / `{ PICKLIST: <id> }` — after swimlane-transforms normalization
 * - `{ MULTI_CUSTOMER: <id> }` — raw item-store format
 * - `{ id: <entryIdx>, value: <customerId> }` — picklist mapper format
 * - `{ id: <customerId> }` — id-only object
 *
 * Matches the resolution priority of the backend `extractCustomerIdsFromValue`.
 */
function extractCustomerIds(value: unknown): number[] {
  if (value === null || value === undefined) return [];
  const entries = Array.isArray(value) ? value : [value];
  const ids: number[] = [];
  for (const entry of entries) {
    if (entry === null || entry === undefined) continue;
    let raw: unknown = entry;
    if (typeof entry === 'object') {
      const obj = entry as Record<string, unknown>;
      raw =
        'MULTI_PICKLIST' in obj
          ? obj.MULTI_PICKLIST
          : 'PICKLIST' in obj
            ? obj.PICKLIST
            : 'MULTI_CUSTOMER' in obj
              ? obj.MULTI_CUSTOMER
              : 'value' in obj
                ? obj.value
                : 'id' in obj
                  ? obj.id
                  : undefined;
    }
    if (raw === undefined || raw === null) continue;
    const parsed = typeof raw === 'number' ? raw : Number(String(raw));
    if (!Number.isFinite(parsed) || parsed <= 0) continue;
    ids.push(parsed);
  }
  return ids;
}

function renderMultiCustomer(
  value: unknown,
  customerLookup: Record<string, CustomerLookupEntry> | undefined,
): ReactNode {
  const ids = extractCustomerIds(value);
  if (ids.length === 0) {
    return '—';
  }

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      {ids.map((id) => {
        const entry = customerLookup?.[String(id)];
        const customer = {
          id: String(id),
          name: entry?.name ?? String(id),
          logoUrl: entry?.logoUrl ?? null,
        };
        return <SwimlaneCustomerChip key={String(id)} customer={customer} />;
      })}
    </span>
  );
}

export const PublishedCustomFieldValue = ({
  field,
  value,
  customerLookup,
}: PublishedCustomFieldValueProps) => {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const body =
    field.type === 'FIELD_TYPE_MULTI_CUSTOMER' && ideaManagerEnabled
      ? renderMultiCustomer(value, customerLookup)
      : field.type === 'FIELD_TYPE_MULTI_CUSTOMER'
        ? '—'
        : formatValue(field, value);

  return (
    <div>
      <dt className="text-sm font-medium text-text-neutral-secondary">{field.name}</dt>
      <dd className="text-sm text-text-neutral whitespace-pre-wrap mt-1">{body}</dd>
    </div>
  );
};
